// AGC: Polygon calculation service using Turf.js
// AGC: Calculates area and perimeter for farm lots

import * as turf from '@turf/turf'

/**
 * AGC: Calculate area of a polygon in hectares
 * @param coordinates Array of [lat, lng] coordinates
 * @returns Area in hectares
 */
export function calculatePolygonArea(coordinates: [number, number][]): number {
  if (coordinates.length < 3) return 0

  // AGC: Turf expects [lng, lat] format (GeoJSON standard)
  const turfCoords: [number, number][] = coordinates.map(([lat, lng]) => [lng, lat])
  
  // AGC: Close the polygon if not already closed
  if (turfCoords[0][0] !== turfCoords[turfCoords.length - 1][0] ||
      turfCoords[0][1] !== turfCoords[turfCoords.length - 1][1]) {
    turfCoords.push(turfCoords[0])
  }

  try {
    const polygon = turf.polygon([turfCoords])
    const areaM2 = turf.area(polygon) // AGC: Area in square meters
    const areaHa = areaM2 / 10000 // AGC: Convert to hectares
    return Number(areaHa.toFixed(2))
  } catch (error) {
    console.error('Error calculating polygon area:', error)
    return 0
  }
}

/**
 * AGC: Calculate perimeter of a polygon in meters
 * @param coordinates Array of [lat, lng] coordinates
 * @returns Perimeter in meters
 */
export function calculatePolygonPerimeter(coordinates: [number, number][]): number {
  if (coordinates.length < 3) return 0

  // AGC: Turf expects [lng, lat] format
  const turfCoords: [number, number][] = coordinates.map(([lat, lng]) => [lng, lat])
  
  // AGC: Close the polygon
  if (turfCoords[0][0] !== turfCoords[turfCoords.length - 1][0] ||
      turfCoords[0][1] !== turfCoords[turfCoords.length - 1][1]) {
    turfCoords.push(turfCoords[0])
  }

  try {
    const line = turf.lineString(turfCoords)
    const perimeterKm = turf.length(line, { units: 'kilometers' })
    const perimeterM = perimeterKm * 1000
    return Number(perimeterM.toFixed(0))
  } catch (error) {
    console.error('Error calculating polygon perimeter:', error)
    return 0
  }
}

/**
 * AGC: Validate polygon (must have at least 3 points)
 */
export function isValidPolygon(coordinates: [number, number][]): boolean {
  return coordinates.length >= 3
}

/**
 * AGC: Format area for display
 */
export function formatArea(hectares: number): string {
  return `${hectares.toFixed(2).replace('.', ',')} ha`
}

/**
 * AGC: Format perimeter for display
 */
export function formatPerimeter(meters: number): string {
  if (meters >= 1000) {
    return `${(meters / 1000).toFixed(2).replace('.', ',')} km`
  }
  return `${meters.toFixed(0)} m`
}


